import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios"
import { StatusCodes } from "http-status-codes"
import { toast } from "react-toastify"
import { getErrorMessage } from "../app/utils/getErrorMessage"
import { getMsalInstance } from "../msalInstance"

const apiDigitalManagerBaseURL = import.meta.env.VITE_API_DIGITALMANAGER_URL
const msalInstance = getMsalInstance()

const contentType = "application/json"

const baseDigitalManagerConfig = {
  baseURL: apiDigitalManagerBaseURL,
  headers: {
    "Content-Type": contentType,
  },
}

const onResponse = (response: AxiosResponse): AxiosResponse => response

const onResponseError = async (error: AxiosError): Promise<AxiosError> => {
  if (
    error?.response?.status &&
    error.response.status > StatusCodes.INTERNAL_SERVER_ERROR
  ) {
    toast.error("Ops! An error occurred while calling the server", {
      toastId: "axiosOnResponseError500",
    })
  }

  throw error instanceof Error ? error : new Error("Unknown error")
}

const onRequest = async (
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  // Inject accessToken header to all API calls
  const accounts = msalInstance.getAllAccounts()
  if (!accounts?.length) {
    throw new Error("User not authenticated")
  }

  const user = accounts[0]
  if (user) {
    try {
      const res = await msalInstance.acquireTokenSilent({
        scopes: [
          "api://4909f345-1cc0-4e61-8a40-62273ae597d5/DigitalManager.ReadWrite.Admin",
        ],
        account: user,
      })

      if (res?.accessToken && config.headers) {
        config.headers["Authorization"] = `Bearer ${res.accessToken}`
        globalThis.localStorage.setItem("lastAccessToken", res.accessToken)
      }
    } catch (error) {
      const tokenAcquisitionError = new Error(
        getErrorMessage(error, "Silent token acquisition failed")
      )

      await msalInstance.acquireTokenRedirect({
        scopes: [
          "api://4909f345-1cc0-4e61-8a40-62273ae597d5/DigitalManager.ReadWrite.Admin",
        ],
        account: user,
      })

      throw tokenAcquisitionError
    }
  }

  return config
}

const onRequestError = (error: AxiosError): Promise<AxiosError> =>
  Promise.reject(error instanceof Error ? error : new Error("Unknown error"))

export const digitalManagerApi = axios.create(baseDigitalManagerConfig)
digitalManagerApi.interceptors.request.use(onRequest, onRequestError)
digitalManagerApi.interceptors.response.use(onResponse, onResponseError)
